// Smart Sash Admin Panel - Hood Usage Report Hook
// Manages filters (cascading), sorting, pagination, and data loading

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  GroupDTO,
  DEFAULT_HOOD_USAGE_SORT,
  EMPTY_HOOD_USAGE_FILTERS,
  HoodUsageFilters,
  HoodUsageSortField,
  HoodUsageSortState,
  InstitutionDTO,
  LabDTO,
  UserDTO,
} from '@/models';
import {
  useGroupStore,
  useDeviceStore,
  useInstitutionStore,
  useLabStore,
  useReportStore,
  useUserStore,
} from '@/store';
import { reportService } from '@/services/api/reportService';
import { useAuth } from '@/services/auth';
import { usePermissions } from '@/hooks/usePermissions';

export function useHoodUsageReport() {
  const [filters, setFilters] = useState<HoodUsageFilters>(EMPTY_HOOD_USAGE_FILTERS);
  const [sort, setSort] = useState<HoodUsageSortState>(DEFAULT_HOOD_USAGE_SORT);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [entitiesLoaded, setEntitiesLoaded] = useState(false);

  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const isFullAccess = hasPermission('FullAccess');

  const reportStore = useReportStore();
  const institutionStore = useInstitutionStore();
  const groupStore = useGroupStore();
  const labStore = useLabStore();
  const userStore = useUserStore();
  const deviceStore = useDeviceStore();

  // Load entities once on mount
  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;

    const loadEntities = async () => {
      try {
        await Promise.all([
          institutionStore.fetchInstitutions(1, 1000),
          groupStore.fetchGroups(1, 1000),
          labStore.fetchLabs(1, 1000),
          userStore.fetchUsers(),
          deviceStore.fetchDevices(1, 1000),
        ]);
        // Reset fake data cache so it uses freshly loaded entities
        reportService.resetCache();
        setEntitiesLoaded(true);
      } catch (error) {
        console.error('Failed to load entities for report:', error);
        setEntitiesLoaded(true);
      }
    };

    loadEntities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cascading filter options
  const allInstitutions = institutionStore.institutions;
  const allGroups = groupStore.groups;
  const allLabs = labStore.labs;
  const allUsers = userStore.users;

  const availableInstitutions: InstitutionDTO[] = useMemo(() => {
    if (isFullAccess) return allInstitutions;
    return allInstitutions.filter((i) => i.id === user?.institutionId);
  }, [isFullAccess, allInstitutions, user?.institutionId]);

  const accessibleInstitutionIds = useMemo(
    () => availableInstitutions.map((i) => i.id),
    [availableInstitutions]
  );

  const availableUsers: UserDTO[] = useMemo(() => {
    if (isFullAccess) return allUsers;
    return allUsers.filter((u) => u.institutionId === user?.institutionId);
  }, [isFullAccess, allUsers, user?.institutionId]);

  const accessibleUserIds = useMemo(
    () => availableUsers.map((u) => u.id),
    [availableUsers]
  );

  const accessibleGroupIds = useMemo(() => {
    const instSet = new Set(accessibleInstitutionIds);
    return allGroups.filter((g) => instSet.has(g.institutionId)).map((g) => g.id);
  }, [allGroups, accessibleInstitutionIds]);

  const accessibleLabIds = useMemo(() => {
    const grpSet = new Set(accessibleGroupIds);
    return allLabs.filter((l) => l.groupId && grpSet.has(l.groupId)).map((l) => l.id);
  }, [allLabs, accessibleGroupIds]);

  const effectiveFilters: HoodUsageFilters = useMemo(() => {
    if (isFullAccess) return filters;
    // Fail-safe: use no-match sentinel when institution scope can't be determined,
    // so zero results are returned rather than all results.
    const noMatch = ['__no_match__'];
    const institutionIds =
      filters.institutionIds.length > 0
        ? filters.institutionIds
        : accessibleInstitutionIds.length > 0 ? accessibleInstitutionIds : noMatch;
    const groupIds =
      filters.groupIds.length > 0
        ? filters.groupIds
        : accessibleGroupIds.length > 0 ? accessibleGroupIds : noMatch;
    const labIds =
      filters.labIds.length > 0
        ? filters.labIds
        : accessibleLabIds.length > 0 ? accessibleLabIds : noMatch;
    const userIds =
      filters.userIds.length > 0
        ? filters.userIds
        : accessibleUserIds.length > 0 ? accessibleUserIds : noMatch;
    return { ...filters, institutionIds, groupIds, labIds, userIds };
  }, [isFullAccess, filters, accessibleInstitutionIds, accessibleGroupIds, accessibleLabIds, accessibleUserIds]);

  const availableGroups: GroupDTO[] = useMemo(() => {
    const accessibleGrpSet = new Set(accessibleGroupIds);
    const base = isFullAccess ? allGroups : allGroups.filter((g) => accessibleGrpSet.has(g.id));
    if (filters.institutionIds.length === 0) return base;
    return base.filter((g) => filters.institutionIds.includes(g.institutionId));
  }, [isFullAccess, allGroups, accessibleGroupIds, filters.institutionIds]);

  const availableLabs: LabDTO[] = useMemo(() => {
    const accessibleLabSet = new Set(accessibleLabIds);
    const base = isFullAccess ? allLabs : allLabs.filter((l) => accessibleLabSet.has(l.id));
    if (filters.groupIds.length === 0) {
      if (filters.institutionIds.length === 0) return base;
      const groupIds = new Set(availableGroups.map((g) => g.id));
      return base.filter((l) => l.groupId && groupIds.has(l.groupId));
    }
    return base.filter((l) => l.groupId && filters.groupIds.includes(l.groupId));
  }, [isFullAccess, allLabs, accessibleLabIds, filters.groupIds, filters.institutionIds, availableGroups]);

  // Fetch sessions when filters/sort/pagination change
  useEffect(() => {
    if (!entitiesLoaded) return;
    reportStore.fetchSessions({ filters: effectiveFilters, sort, page, pageSize }).catch(() => {
      // Error is handled in the store
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveFilters, sort, page, pageSize, entitiesLoaded]);

  // Filter setters with cascading resets
  const setInstitutionIds = useCallback((ids: string[]) => {
    setFilters((prev) => ({
      ...prev,
      institutionIds: ids,
      groupIds: [],
      labIds: [],
    }));
    setPage(1);
  }, []);

  const setGroupIds = useCallback((ids: string[]) => {
    setFilters((prev) => ({
      ...prev,
      groupIds: ids,
      labIds: [],
    }));
    setPage(1);
  }, []);

  const setLabIds = useCallback((ids: string[]) => {
    setFilters((prev) => ({ ...prev, labIds: ids }));
    setPage(1);
  }, []);

  const setUserIds = useCallback((ids: string[]) => {
    setFilters((prev) => ({ ...prev, userIds: ids }));
    setPage(1);
  }, []);

  const setDateRange = useCallback((dateFrom: string | null, dateTo: string | null) => {
    setFilters((prev) => ({ ...prev, dateFrom, dateTo }));
    setPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(EMPTY_HOOD_USAGE_FILTERS);
    setPage(1);
  }, []);

  const handleSort = useCallback(
    (field: HoodUsageSortField) => {
      setSort((prev) => ({
        field,
        order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc',
      }));
      setPage(1);
    },
    []
  );

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setPage(1);
  }, []);

  return {
    sessions: reportStore.sessions,
    pagination: reportStore.pagination,
    isLoading: reportStore.isLoading || !entitiesLoaded,
    error: reportStore.error,

    filters,
    setInstitutionIds,
    setGroupIds,
    setLabIds,
    setUserIds,
    setDateRange,
    clearFilters,

    availableInstitutions,
    availableGroups,
    availableLabs,
    availableUsers,

    sort,
    setSort: handleSort,

    page,
    pageSize,
    setPage,
    setPageSize: handlePageSizeChange,
  };
}
